import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuota } from '../../components/quota/useQuota';
import { ApiError } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import { retryGenerationRequest } from '../generation/generationApi';

export interface RetryFlow {
  isSubmitting: boolean;
  error: string | null;
  /** Queues the failed brief again and navigates to the request it runs under. */
  submit: () => void;
}

/** Message for the domain errors a retry can provoke. */
function retryMessage(caught: unknown): string {
  if (caught instanceof ApiError) {
    switch (caught.code) {
      case 'REQUEST_NOT_FAILED':
        return 'This post is no longer marked as failed. Reload it to see where it stands.';
      case 'QUOTA_EXCEEDED':
        return "You've used this month's posts. The counter resets at the start of next month.";
      case 'QUEUE_UNAVAILABLE':
        return 'We are not taking new work right now. Try again in a minute.';
      default:
        return caught.message;
    }
  }
  return getErrorMessage(caught, 'That retry did not start. Try again.');
}

/**
 * "Try again" on a failed run.
 *
 * Like a rejection, the backend opens a **new** request for the run and returns
 * it, so the screen follows it to `/review/{new_id}`, the same route the brief
 * form hands off to. Unlike a rejection it spends no attempt off the cap: the
 * new row keeps the failed one's `attempt`. The call is idempotent server-side,
 * so a double click lands on the same retry rather than queueing two.
 */
export function useRetryRequest(businessId: string, requestId: string): RetryFlow {
  const navigate = useNavigate();
  const { refresh: refreshQuota } = useQuota();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(() => {
    if (isSubmitting) return;

    setError(null);
    setIsSubmitting(true);

    void (async () => {
      try {
        const retried = await retryGenerationRequest(businessId, requestId);
        setIsSubmitting(false);
        // The retry is charged like any other run; the counter moves with it.
        refreshQuota();
        void navigate(`/review/${retried.id}`);
      } catch (caught) {
        setIsSubmitting(false);
        setError(retryMessage(caught));
      }
    })();
  }, [businessId, isSubmitting, navigate, refreshQuota, requestId]);

  return { isSubmitting, error, submit };
}
